import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/use-auth-store';
import { useSocketContext } from '@/contexts/socket-context';
import { subscriptionService, type SubscriptionPlan, type CreateSubscriptionResponse, type Subscription } from '@/services/subscription/subscription.service';
import { useSubscription } from '@/hooks/subscription/use-subscription';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StyledInput } from '@/components/ui/styled-input';
import { StyledLabel } from '@/components/ui/styled-label';
import { toast } from 'sonner';
import { CreditCard, Calendar, CheckCircle2 } from 'lucide-react';

const MONTH_OPTIONS = [1, 3, 6, 12];

interface CheckoutFormProps {
  planId?: string | null;
  subscriptionId?: string | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  className?: string;
  suppressPaymentSuccessToast?: boolean;
}

export function CheckoutForm({ planId, subscriptionId, onSuccess, onCancel, className, suppressPaymentSuccessToast = false }: CheckoutFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { socket, isConnected } = useSocketContext();
  const queryClient = useQueryClient();

  // Only fetch subscription when we actually need it:
  const shouldFetchSubscription = !!subscriptionId || !planId;
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery({
    queryKey: ['subscription', 'me'],
    enabled: shouldFetchSubscription,
    queryFn: async () => {
      const response = await subscriptionService.getMySubscription();
      // Handle 404 object from handleApiRequest specifically for checkout flow
      if (!response) return null;
      if ('statusCode' in response && response.statusCode === 404) return null;
      return ('data' in response && response.data) ? response.data : (response as Subscription);
    },
    staleTime: 5 * 60 * 1000,
  });

  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState<number>(1);
  const [customMonths, setCustomMonths] = useState<string>('');
  const [isCustomMode, setIsCustomMode] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'pending' | 'success' | 'failed'>('idle');
  const [transition, setTransition] = useState<any>(null);
  const [isLoadingTransition, setIsLoadingTransition] = useState(false);
  const errorShownRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (planId) {
      loadPlan();
    }
  }, [planId, isAuthenticated]);

  useEffect(() => {
    errorShownRef.current = false;
    setPlan(null);
    setIsLoadingPlan(true);
  }, [planId, subscriptionId]);

  useEffect(() => {
    if (planId && plan && (subscription as Subscription)?.status === 'ACTIVE' && !isLoadingSubscription) {
      loadTransition(planId);
    }
  }, [planId, plan, subscription, isLoadingSubscription]);

  useEffect(() => {
    if (!isAuthenticated || planId) return;

    if (subscriptionId) {
      if (!isLoadingSubscription) {
        if (subscription) {
          if ((subscription as Subscription)?.id === subscriptionId) {
            setPlan((subscription as Subscription)?.plan || null);
            setIsLoadingPlan(false);
          } else if (!errorShownRef.current) {
            errorShownRef.current = true;
            toast.error(t('checkout.errors.noPlanOrSubscription'));
            setIsLoadingPlan(false);
          }
        } else if (!errorShownRef.current) {
          errorShownRef.current = true;
          toast.error(t('checkout.errors.noPlanOrSubscription'));
          setIsLoadingPlan(false);
        }
      }
    } else {
      if (!isLoadingSubscription) {
        if (subscription) {
          setPlan((subscription as Subscription)?.plan || null);
          setIsLoadingPlan(false);
        } else if (!errorShownRef.current) {
          errorShownRef.current = true;
          toast.error(t('checkout.errors.noPlanOrSubscription'));
          setIsLoadingPlan(false);
        }
      }
    }
  }, [subscriptionId, subscription, planId, isLoadingSubscription, isAuthenticated, t]);

  useEffect(() => {
    if (socket && paymentId) {
      const handlePaymentSuccess = (data: { paymentId: string; subscriptionId: string; invoiceNumber?: string }) => {
        if (data.paymentId === paymentId) {
          setPaymentStatus('success');
          queryClient.invalidateQueries({ queryKey: ['subscription'] });
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          // Only show payment success toast if not suppressed (e.g., during onboarding, parent will show setup complete toast)
          if (!suppressPaymentSuccessToast) {
            toast.success(t('checkout.toasts.paymentSuccessful'));
          }
          if (onSuccess) {
            onSuccess();
          }
        }
      };

      const handlePaymentFailed = (data: { paymentId: string; reason: string }) => {
        if (data.paymentId === paymentId) {
          setPaymentStatus('failed');
          toast.error(t('checkout.toasts.paymentFailed', { reason: data.reason }));
        }
      };

      socket.on('payment:success', handlePaymentSuccess);
      socket.on('payment:failed', handlePaymentFailed);

      return () => {
        socket.off('payment:success', handlePaymentSuccess);
        socket.off('payment:failed', handlePaymentFailed);
      };
    }
  }, [socket, paymentId, t, onSuccess, queryClient, suppressPaymentSuccessToast]);

  const loadPlan = async () => {
    if (!planId) return;
    setIsLoadingPlan(true);
    try {
      const response = await subscriptionService.getPlanById(planId) as { data?: SubscriptionPlan } | SubscriptionPlan;
      let plan: SubscriptionPlan | null = null;
      if ('data' in response && response.data) {
        plan = response.data;
      } else if (response && !('data' in response)) {
        plan = response as SubscriptionPlan;
      }
      setPlan(plan);
    } catch (error) {
      console.error('Failed to load plan:', error);
      toast.error(t('checkout.errors.failedToLoadPlan'));
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const loadTransition = async (planIdToCheck: string) => {
    setIsLoadingTransition(true);
    try {
      const response = await subscriptionService.getSubscriptionTransition(planIdToCheck) as { data?: unknown } | unknown;
      setTransition(('data' in (response as { data?: unknown }) ? (response as { data?: unknown }).data : response) as typeof transition);
    } catch (error) {
      console.error('Failed to load transition:', error);
    } finally {
      setIsLoadingTransition(false);
    }
  };

  const handleMonthsChange = (months: number) => {
    setSelectedMonths(months);
    setIsCustomMode(false);
    setCustomMonths('');
  };

  const handleCustomMonthsChange = (value: string) => {
    setCustomMonths(value);
    if (value && !isNaN(Number(value)) && Number(value) > 0) {
      setSelectedMonths(Number(value));
    }
  };

  const getTotalAmount = () => {
    if (!plan) return 0;
    if (transition?.action === 'UPGRADE' && transition?.proratedAmount) {
      return transition.proratedAmount * selectedMonths;
    }
    return plan.priceInCents * selectedMonths;
  };

  const isUpgrade = transition?.action === 'UPGRADE';

  const handleCheckout = async () => {
    if (!plan) {
      toast.error(t('checkout.errors.noPlanSelected'));
      return;
    }

    if (!isConnected) {
      toast.error(t('checkout.errors.waitingForConnection'));
      return;
    }

    const months = isCustomMode && customMonths ? Number(customMonths) : selectedMonths;
    if (months < 1 || months > 120) {
      toast.error(t('checkout.errors.invalidMonths'));
      return;
    }

    setIsProcessingPayment(true);
    setPaymentStatus('idle');

    try {
      const checkoutData: { planId?: string; subscriptionId?: string; months: number } = {
        months,
      };

      if (planId) {
        checkoutData.planId = planId;
      } else if (subscriptionId || (subscription && typeof subscription === 'object' && subscription !== null && 'id' in subscription)) {
        checkoutData.subscriptionId = subscriptionId || (subscription && typeof subscription === 'object' && subscription !== null && 'id' in subscription ? String(subscription.id) : undefined);
      } else {
        toast.error(t('checkout.errors.noPlanOrSubscription'));
        setIsProcessingPayment(false);
        return;
      }

      const response = await subscriptionService.checkout(checkoutData) as CreateSubscriptionResponse;
      const payment = response.payment || (response as any).payment;
      
      if (!payment) {
        toast.error(t('checkout.errors.paymentInfoUnavailable'));
        setPaymentStatus('failed');
        setIsProcessingPayment(false);
        return;
      }

      setPaymentId(payment.id);
      setPaymentLinkUrl(payment.paymentLinkUrl || null);
      setPaymentStatus('pending');
      setIsProcessingPayment(false);

      if (payment.paymentLinkUrl) {
        window.open(payment.paymentLinkUrl, '_blank', 'noopener,noreferrer');
        toast.success(t('checkout.toasts.paymentWindowOpened'));
      } else {
        toast.warning(t('checkout.toasts.paymentUrlMissing'));
        setPaymentStatus('failed');
      }
    } catch (error: any) {
      console.error('Failed to checkout:', error);
      const errorMessage = error?.response?.data?.message || t('checkout.errors.failedToCheckout');
      toast.error(errorMessage);
      setPaymentStatus('failed');
      setIsProcessingPayment(false);
    }
  };

  if (isLoadingPlan) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p className="text-gray-600">{t('checkout.loading')}</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">{t('checkout.errors.noPlanFound')}</p>
        <Button onClick={() => {
          if (onCancel) {
            onCancel();
          }
        }} className="mt-4">
          {t('checkout.backToBilling')}
        </Button>
      </div>
    );
  }

  if (paymentStatus === 'success') {
    return (
      <div className="text-center py-12">
        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">{t('checkout.paymentSuccessful')}</h2>
        <p className="text-gray-600">{t('checkout.redirecting')}</p>
      </div>
    );
  }

  if (paymentStatus === 'failed') {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 text-6xl mb-4">✗</div>
        <h2 className="text-2xl font-bold mb-2">{t('checkout.paymentFailed')}</h2>
        <p className="text-gray-600 mb-4">{t('checkout.paymentFailedMessage')}</p>
        <Button onClick={() => {
          setPaymentStatus('idle');
          setPaymentLinkUrl(null);
        }}>{t('checkout.tryAgain')}</Button>
      </div>
    );
  }

  if (paymentStatus === 'pending') {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <h2 className="text-2xl font-bold mb-2">{t('checkout.processingPayment')}</h2>
        <p className="text-gray-600">
          {isConnected
            ? t('checkout.completePaymentInModal')
            : t('checkout.connectingToPaymentService')}
        </p>
      </div>
    );
  }

  const totalAmount = getTotalAmount();

  return (
    <div className={className}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('checkout.selectDuration')}</CardTitle>
              <CardDescription>{t('checkout.selectDurationDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {MONTH_OPTIONS.map((months) => (
                  <Button
                    key={months}
                    variant={selectedMonths === months && !isCustomMode ? 'default' : 'outline'}
                    onClick={() => handleMonthsChange(months)}
                    className="h-auto py-4 flex flex-col items-center gap-2"
                  >
                    <span className="text-2xl font-bold">{months}</span>
                    <span className="text-xs">
                      {months === 1 ? t('checkout.month') : t('checkout.months')}
                    </span>
                  </Button>
                ))}
              </div>

              <div className="pt-4 border-t">
                <Button
                  variant={isCustomMode ? 'default' : 'outline'}
                  onClick={() => {
                    setIsCustomMode(true);
                    setCustomMonths('');
                  }}
                  className="w-full"
                >
                  {t('checkout.customMonths')}
                </Button>

                {isCustomMode && (
                  <div className="mt-4">
                    <StyledLabel htmlFor="customMonths">{t('checkout.enterMonths')}</StyledLabel>
                    <StyledInput
                      id="customMonths"
                      type="number"
                      min="1"
                      max="120"
                      value={customMonths}
                      onChange={(e) => handleCustomMonthsChange(e.target.value)}
                      placeholder={t('checkout.enterMonthsPlaceholder')}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('checkout.planDetails')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">{t('checkout.planName')}</p>
                <p className="text-lg font-semibold">{plan.displayName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('checkout.monthlyPrice')}</p>
                <p className="text-lg font-semibold">
                  ${plan.priceInCents.toLocaleString('en-US')} / {t('checkout.month')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>{t('checkout.orderSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isUpgrade && transition?.requiresProrating && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm space-y-1">
                  <p className="font-semibold text-blue-900">{t('checkout.proratingBreakdown')}</p>
                  {transition.proratedCredit !== undefined && (
                    <div className="flex justify-between">
                      <span>{t('checkout.creditFromUnusedTime')}</span>
                      <span>-${transition.proratedCredit.toLocaleString('en-US')}</span>
                    </div>
                  )}
                  {transition.proratedAmount !== undefined && (
                    <div className="flex justify-between font-semibold pt-1 border-t border-blue-200">
                      <span>{t('checkout.proratedAmount')}</span>
                      <span>${transition.proratedAmount.toLocaleString('en-US')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <span>{t('checkout.duration')}</span>
                <span className="font-semibold">{selectedMonths} {selectedMonths === 1 ? t('checkout.month') : t('checkout.months')}</span>
              </div>
              <div className="pt-4 border-t flex justify-between items-center text-lg font-bold">
                <span>{t('checkout.total')}</span>
                <span>${totalAmount.toLocaleString('en-US')}</span>
              </div>

              <Button
                onClick={handleCheckout}
                disabled={isProcessingPayment || !isConnected}
                className="w-full mt-6"
                size="lg"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {isProcessingPayment ? t('checkout.processing') : t('checkout.proceedToPayment')}
              </Button>

              {!isConnected && (
                <p className="text-yellow-600 text-xs text-center mt-2">
                  {t('checkout.waitingForConnection')}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
